import { useMemo, useEffect, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Spin } from 'antd';

import useHeaderTitle from '../../hooks/useHeaderTitle';
import PreparingFood from './components/PreparingFood';
import FoodReady from './components/FoodReady';
import OrderCompleted from './components/OrderCompleted';
import OrderCancelled from './components/OrderCancelled';
import OrderRemovedByAdmin from './components/OrderRemovedByAdmin';

import './ReceiptPage.less';

const ReceiptPage = (props) => {
	const location = useLocation();
	const history = useHistory();
	const pollingInterval = useRef(null);
	const isInitialLoad = useRef(true);

	const { getOrderByOrderId, resetPage, receiptPageProps } = props;
	const { loading, order, meta } = receiptPageProps;

	const isPendingOrPreparing = order?.order_status === 'pending' || order?.order_status === 'preparing';
	useHeaderTitle('金飯碗 Gold Rice Bowl', isPendingOrPreparing);

	// Get order_id and token from URL search query
	const { orderId, token } = useMemo(() => {
		const searchParams = new URLSearchParams(location.search);
		return {
			orderId: searchParams.get('order_id'),
			token: searchParams.get('token')
		};
	}, [location.search]);

	// Redirect to not-found if params are missing
	useEffect(() => {
		if (!orderId || !token) {
			history.replace('/not-found');
		}
	}, [orderId, token, history]);

	// Fetch order data when component mounts or params change
	useEffect(() => {
		if (orderId && token) {
			getOrderByOrderId(orderId, token);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderId, token]);

	// Set up polling to refetch order every 5 seconds
	useEffect(() => {
		if (orderId && token && order) {
			// Mark initial load as complete once we have order data
			isInitialLoad.current = false;

			// Clear any existing interval
			if (pollingInterval.current) {
				clearInterval(pollingInterval.current);
			}

			// Set up new polling interval
			pollingInterval.current = setInterval(() => {
				getOrderByOrderId(orderId, token);
			}, 5000);

			// Cleanup interval on unmount or when dependencies change
			return () => {
				if (pollingInterval.current) {
					clearInterval(pollingInterval.current);
				}
			};
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderId, token, order]);

	// Handle 404 error (invalid order_id or token)
	useEffect(() => {
		if (meta?.code === 404) {
			history.replace('/not-found');
		}
	}, [meta, history]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			resetPage();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Handle case where params are missing (will redirect)
	if (!orderId || !token) {
		return null;
	}

	// Show loading state only on initial load
	if (isInitialLoad.current && (loading || !order)) {
		return (
			<div className="receipt-container">
				<div style={{ padding: '20px', textAlign: 'center', marginTop: '50px' }}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	// If not initial load and no order, return null (shouldn't happen)
	if (!order) {
		return null;
	}

	// Determine which component to render based on order_status
	const renderOrderComponent = () => {
		const { order_status } = order;

		if (order_status === 'cancelled') {
			return (
				<OrderCancelled
					orderId={orderId}
					orderData={order}
				/>
			);
		}

		if (order_status === 'pending' || order_status === 'preparing') {
			return (
				<PreparingFood
					orderId={orderId}
					qrCode={order.token}
					orderData={order}
				/>
			);
		}

		if (order_status === 'ready') {
			return (
				<FoodReady
					orderId={orderId}
					qrCode={order.token}
					orderData={order}
				/>
			);
		}

		if (order_status === 'completed') {
			return (
				<OrderCompleted
					orderId={orderId}
					orderData={order}
				/>
			);
		}

		if (order_status === 'removed_by_admin') {
			return (
				<OrderRemovedByAdmin
					orderId={orderId}
					orderData={order}
				/>
			);
		}

		return null;
	};

	return (
		<div className="receipt-container">
			{renderOrderComponent()}
		</div>
	);
};

export default ReceiptPage;
